if (typeof CartGifting !== 'function') {

  class CartGifting extends HTMLElement {

    constructor() {
      super();
      this.initialized = false;
      this.isProcessingGift = false; // Bandera para evitar eliminación durante agregado
    }

    connectedCallback() {
      // Este método se llama cada vez que el elemento se añade al DOM
      if (!this.initialized) {
        this.init();
        this.initialized = true;
      }
    }

    init() {
      // Inicializar propiedades
      this.threshold = Number(this.getAttribute('data-threshold'));
      this.cartTotal = Number(this.getAttribute('data-cart-total'));
      this.productCards = this.querySelectorAll('.cart-gifting__product');
      
      // Verificar estado inicial del carrito
      this.checkCartState();
      
      // Mostrar/ocultar según el umbral alcanzado
      this.updateVisibility();
      
      // Escuchar actualizaciones del carrito
      this._cartUpdatedHandler = () => {
        setTimeout(() => this.updateCartTotal(), 100);
      };
      document.addEventListener('cart:updated', this._cartUpdatedHandler);
    }

    disconnectedCallback() {
      // Limpiar listener cuando el elemento se elimina del DOM
      if (this._cartUpdatedHandler) {
        document.removeEventListener('cart:updated', this._cartUpdatedHandler);
      }
    }

    async checkCartState() {
      // Verificación inicial: eliminar productos gifting si no hay productos normales
      try {
        // Resetear bandera por seguridad al inicializar
        this.isProcessingGift = false;
        
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await response.json();
        
        const cartTotalWithoutGifts = this.calculateTotalWithoutGifts(cart);
        const normalProductsCount = cart.items.filter(item => 
          !item.properties || item.properties._gifting !== 'true'
        ).length;
        
        const hasGiftingProducts = cart.items.some(item => 
          item.properties && item.properties._gifting === 'true'
        );
        
        // Eliminar productos gifting si: no hay productos normales O total sin regalos < umbral
        const shouldRemove = hasGiftingProducts && (
          normalProductsCount === 0 || 
          cartTotalWithoutGifts < this.threshold
        );
        if (shouldRemove) {
          await this.removeAllGiftingProducts(cart);
          
          if (typeof refreshCart === 'function') {
            refreshCart();
          } else {
            window.location.reload();
          }
        }
      } catch (error) {
        // Error silencioso
        this.isProcessingGift = false; // Resetear en caso de error
      }
    }

    calculateTotalWithoutGifts(cart) {
      // Calcular el total del carrito excluyendo productos gifting
      return cart.items.reduce((total, item) => {
        // Solo sumar si NO es un producto gifting
        if (!item.properties || item.properties._gifting !== 'true') {
          return total + item.final_line_price;
        }
        return total;
      }, 0);
    }

    updateVisibility() {
      // Si está oculto para la cart page, no cambiar la visibilidad
      if (this.classList.contains('cart-gifting--hidden')) {
        return;
      }
      
      const cartTotal = Number(this.getAttribute('data-cart-total'));
      
      // Mostrar solo si el carrito alcanzó el umbral
      if (cartTotal >= this.threshold) {
        this.style.display = 'block';
      } else {
        this.style.display = 'none';
      }
    }

    async updateCartTotal() {
      try {
        // Actualizar el total del carrito
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await response.json();
        
        // Calcular el total SIN productos gifting
        const cartTotalWithoutGifts = this.calculateTotalWithoutGifts(cart);
        
        // Contar productos normales (sin gifting)
        const normalProductsCount = cart.items.filter(item => 
          !item.properties || item.properties._gifting !== 'true'
        ).length;
        
        this.setAttribute('data-cart-total', cartTotalWithoutGifts);
        this.cartTotal = cartTotalWithoutGifts;
        this.updateVisibility();
        
        const hasGiftingProducts = cart.items.some(item => 
          item.properties && item.properties._gifting === 'true'
        );
        
        // SIEMPRE eliminar productos gifting si:
        // 1. No hay productos normales en el carrito (normalProductsCount === 0) - INDEPENDIENTE de isProcessingGift
        // 2. O el total sin gifting está por debajo del umbral (solo si NO estamos procesando)
        const shouldRemove = hasGiftingProducts && (
          normalProductsCount === 0 || 
          (!this.isProcessingGift && cartTotalWithoutGifts < this.threshold)
        );
        
        if (shouldRemove) {
          // Marcar que estamos procesando para evitar loops
          const wasProcessing = this.isProcessingGift;
          this.isProcessingGift = true;
          
          await this.removeAllGiftingProducts(cart);
          
          // Restaurar el estado previo
          this.isProcessingGift = wasProcessing;
          
          // Refrescar el carrito después de eliminar
          if (typeof refreshCart === 'function') {
            refreshCart();
          }
        }
        
        this.updateCardStates(cart);
      } catch (error) {
        // Error silencioso al actualizar el carrito
        // Resetear bandera en caso de error
        this.isProcessingGift = false;
      }
    }

    updateCardStates(cart) {
      // Actualizar el estado de las cards según los productos en el carrito
      this.productCards.forEach(card => {
        const productId = card.getAttribute('data-product-id');
        const isInCart = cart.items.some(item => item.product_id == productId);
        
        if (isInCart) {
          card.classList.add('cart-gifting__product--selected');
          card.setAttribute('data-in-cart', 'true');
          card.style.cursor = 'pointer'; // Permitir click para eliminar
        } else {
          card.classList.remove('cart-gifting__product--selected');
          card.setAttribute('data-in-cart', 'false');
          card.style.cursor = 'pointer';
        }
      });
    }

    async handleCardClick(event, card) {
      // Prevenir múltiples clicks
      if (this.isProcessingGift) {
        return;
      }

      const isInCart = card.getAttribute('data-in-cart') === 'true';
      const variantId = card.getAttribute('data-variant-id');
      const productId = card.getAttribute('data-product-id');

      // Marcar visualmente como procesando
      card.style.opacity = '0.6';
      card.style.cursor = 'wait';

      // Activar bandera
      this.isProcessingGift = true;

      try {
        // Obtener el carrito actual
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await cartResponse.json();

        // Si el producto YA está en el carrito → ELIMINARLO
        if (isInCart) {
          await this.removeGiftingProduct(cart, productId);
        } 
        // Si el producto NO está en el carrito → AGREGAR (eliminando otros primero)
        else {
          // Primero eliminar cualquier otro producto gifting
          await this.removeAllGiftingProducts(cart);
          
          // Luego agregar el nuevo producto
          await this.addGiftingProduct(variantId);
        }

        // Restaurar cursor
        card.style.opacity = '1';
        card.style.cursor = 'pointer';

        // Desactivar bandera ANTES de disparar el evento
        this.isProcessingGift = false;

        // Disparar evento de actualización del carrito
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Refrescar el cart drawer
        if (typeof refreshCart === 'function') {
          refreshCart();
        } else {
          // Fallback: recargar la página del carrito
          window.location.reload();
        }

      } catch (error) {
        this.isProcessingGift = false; // Resetear en caso de error
        card.style.opacity = '1';
        card.style.cursor = 'pointer';
        alert('Error: ' + error.message);
      }
    }

    async removeGiftingProduct(cart, productId) {
      // Encontrar el line item del producto gifting
      const giftItem = cart.items.find(item => 
        item.product_id == productId && item.properties && item.properties._gifting === 'true'
      );

      if (giftItem) {
        const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: giftItem.key,
            quantity: 0
          })
        });

        if (!response.ok) {
          throw new Error('Error al eliminar el regalo');
        }
      }
    }

    async removeAllGiftingProducts(cart) {
      // Encontrar todos los productos gifting en el carrito
      const giftItems = cart.items.filter(item => 
        item.properties && item.properties._gifting === 'true'
      );

      // Eliminar cada uno
      for (const item of giftItems) {
        await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: item.key,
            quantity: 0
          })
        });
      }
    }

    async addGiftingProduct(variantId) {
      const formData = {
        'items': [{
          'id': Number(variantId),
          'quantity': 1,
          'properties': {
            '_gifting': 'true'
          }
        }]
      };

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const result = await response.json();
        const errorMsg = result.description || result.message || 'Error al añadir el regalo';
        throw new Error(errorMsg);
      }
    }

  }

  if (typeof customElements.get('cart-gifting') == 'undefined') {
    customElements.define('cart-gifting', CartGifting);
  }

  // Delegación de eventos: capturar clics en productos gifting aunque el elemento se inserte dinámicamente
  // (al refrescar el carrito el cart-gifting se reemplaza y los listeners se pierden)
  document.addEventListener('click', (e) => {
    const card = e.target.closest('.cart-gifting__product');
    if (card) {
      const cartGifting = card.closest('cart-gifting');
      if (cartGifting && typeof cartGifting.handleCardClick === 'function') {
        e.preventDefault();
        cartGifting.handleCardClick(e, card);
      }
    }
  });

}
